/**
 * Voucher edit affordances — inline editing primitives shared across voucher
 * templates (Activity, Hotel, Flight, …).
 *
 * All three components honour a `locked` prop (status=ISSUED). When locked
 * they replace the pencil with a Lock icon and never expose an editor.
 *
 * PDF-safety: like VoucherPrimitives.js, NO flexbox is used anywhere in
 * this file. Inline-block + CSS-table layouts only.
 *
 * Components:
 *  - InlineEditableField  — single value: text or date.
 *  - EditableArraySectionCard — section card whose contents are an array
 *    field (one item per line in the editor).
 *  - RefRow — small "label : value" reference row, optionally inline-editable.
 */
import React, { useState, useEffect } from 'react';
import { Pencil, Lock } from 'lucide-react';
import {
  SectionCard,
  BulletList,
  InlineHtmlOrText,
  toArray,
  has,
} from './VoucherPrimitives';

// ---------------------------------------------------------------------------
// InlineEditableField — supports a "text" variant (small textarea) and a
// "date" variant (HTML5 date input). Only renders the pencil/editor UI
// when `editable` is true. Otherwise it's a plain text/date display,
// identical to the read-only voucher view.
//
// When `locked` is true, renders the value + Lock icon (no editor, no
// pencil). `editable` is ignored in locked mode.
//
// onSave(value) returns a Promise. The component awaits it and only closes
// the editor on success. Escape key and Cancel button both discard.
// ---------------------------------------------------------------------------
export function InlineEditableField({
  value,
  variant = 'text',            // 'text' | 'date'
  editable = false,
  locked = false,              // status=ISSUED → show Lock instead of pencil
  onSave,
  displayClassName = '',
  displayStyle = {},
  inputStyle = {},
  formatValue,                 // optional formatter for display
  textareaRows = 2,
  ariaLabel = 'Edit field',
  testIdPrefix = 'field',
}) {
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState(value || '');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isEditing) setDraft(value || '');
  }, [value, isEditing]);

  const cancel = () => {
    setDraft(value || '');
    setIsEditing(false);
  };
  const save = async () => {
    const next = String(draft || '').trim();
    if (next === String(value || '').trim()) {
      setIsEditing(false);
      return;
    }
    setSaving(true);
    try {
      if (typeof onSave === 'function') await onSave(next);
      setIsEditing(false);
    } catch (err) {
      console.error('inline save failed:', err);
    } finally {
      setSaving(false);
    }
  };

  const display = typeof formatValue === 'function' ? formatValue(value) : value;

  // Locked (voucher issued) — value + Lock icon, no editor affordance.
  if (locked) {
    return (
      <span
        style={{ display: 'inline-block', verticalAlign: 'middle' }}
        data-testid={`${testIdPrefix}-locked-wrapper`}
      >
        <span
          className={displayClassName}
          style={displayStyle}
          data-testid={`${testIdPrefix}-display`}
        >
          {display}
        </span>
        <Lock
          size={12}
          aria-label="Locked — voucher issued"
          data-testid={`${testIdPrefix}-lock-icon`}
          style={{ marginLeft: 8, color: '#9ca3af', verticalAlign: 'middle' }}
        />
      </span>
    );
  }

  if (!editable) {
    return (
      <span
        className={displayClassName}
        style={displayStyle}
        data-testid={`${testIdPrefix}-display`}
      >
        {display}
      </span>
    );
  }

  if (!isEditing) {
    return (
      <span
        style={{ display: 'inline-block', verticalAlign: 'middle' }}
        data-testid={`${testIdPrefix}-display-wrapper`}
      >
        <span
          className={displayClassName}
          style={displayStyle}
          data-testid={`${testIdPrefix}-display`}
        >
          {display}
        </span>
        <button
          type="button"
          onClick={() => setIsEditing(true)}
          title={ariaLabel}
          aria-label={ariaLabel}
          data-testid={`${testIdPrefix}-edit-btn`}
          className="pencil-edit-btn"
          style={{
            marginLeft: '8px',
            background: 'transparent',
            border: '1px solid #e5e7eb',
            borderRadius: '4px',
            padding: '2px 6px',
            cursor: 'pointer',
            color: '#9ca3af',
            verticalAlign: 'middle',
            lineHeight: 1,
          }}
        >
          <Pencil size={12} />
        </button>
      </span>
    );
  }

  // editing mode
  const sharedInputStyle = {
    border: '2px solid #0891b2',
    borderRadius: '4px',
    padding: '6px 10px',
    outline: 'none',
    background: '#f0f9ff',
    color: '#111827',
    ...inputStyle,
  };

  return (
    <span
      style={{ display: 'inline-block', verticalAlign: 'middle' }}
      data-testid={`${testIdPrefix}-editor`}
    >
      {variant === 'date' ? (
        <input
          type="date"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          autoFocus
          onKeyDown={(e) => {
            if (e.key === 'Escape') cancel();
          }}
          style={{ fontSize: '15px', fontFamily: 'Inter, sans-serif', ...sharedInputStyle }}
          data-testid={`${testIdPrefix}-input`}
        />
      ) : (
        <textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          rows={textareaRows}
          autoFocus
          onKeyDown={(e) => {
            if (e.key === 'Escape') cancel();
          }}
          style={{
            width: '100%',
            minWidth: '300px',
            resize: 'vertical',
            lineHeight: '1.2',
            ...sharedInputStyle,
          }}
          data-testid={`${testIdPrefix}-input`}
        />
      )}
      <button
        type="button"
        onClick={save}
        disabled={saving}
        data-testid={`${testIdPrefix}-save`}
        style={{
          marginLeft: '8px',
          padding: '6px 16px',
          background: '#0891b2',
          color: '#fff',
          border: 'none',
          borderRadius: '4px',
          fontSize: '12px',
          fontWeight: 600,
          cursor: saving ? 'not-allowed' : 'pointer',
          opacity: saving ? 0.7 : 1,
          verticalAlign: 'middle',
        }}
      >
        {saving ? 'Saving…' : 'Save'}
      </button>
      <button
        type="button"
        onClick={cancel}
        data-testid={`${testIdPrefix}-cancel`}
        style={{
          marginLeft: '6px',
          padding: '6px 14px',
          background: 'transparent',
          color: '#6b7280',
          border: '1px solid #d1d5db',
          borderRadius: '4px',
          fontSize: '12px',
          cursor: 'pointer',
          verticalAlign: 'middle',
        }}
      >
        Cancel
      </button>
    </span>
  );
}

// ---------------------------------------------------------------------------
// EditableArraySectionCard — wraps SectionCard with an inline edit affordance
// for array (or array-or-string) fields. Pencil button in the title bar
// opens a textarea with one item per line; Save splits by newline → array
// and invokes onFieldSave(fieldKey, arr).
//
// When `editable` is false this behaves exactly like SectionCard + BulletList
// (PDF output unaffected). When `locked` is true a Lock icon replaces the
// pencil and the editor can never be opened.
// ---------------------------------------------------------------------------
export function EditableArraySectionCard({
  title,
  icon,
  accent,
  testId,
  className = '',
  value,             // array or string from voucher data
  fieldKey,          // backend PATCH key
  editable = false,
  locked = false,    // status=ISSUED → show Lock in header, no edit affordance
  onFieldSave,
  kind = 'plain',    // bullet glyph kind: plain | include | exclude
  alwaysShowWhenEditable = true,
}) {
  const items = toArray(value);
  const useBullets = items.length > 1;
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);

  const startEdit = () => {
    const initial = Array.isArray(value) ? value.join('\n') : value || '';
    setDraft(initial);
    setIsEditing(true);
  };
  const cancel = () => {
    setIsEditing(false);
  };
  const save = async () => {
    const lines = String(draft || '')
      .split(/\r?\n/)
      .map((s) => s.trim())
      .filter(Boolean);
    setSaving(true);
    try {
      await onFieldSave?.(fieldKey, lines);
      setIsEditing(false);
    } catch (e) {
      console.error('array save failed:', e);
    } finally {
      setSaving(false);
    }
  };

  // Hide entire card when not editable / not locked AND no data — keeps PDF clean.
  if (!editable && !locked && items.length === 0 && !has(value)) return null;
  if (editable && items.length === 0 && !has(value) && !alwaysShowWhenEditable) return null;
  if (locked && items.length === 0 && !has(value)) return null;

  const pencil = (
    <button
      type="button"
      onClick={startEdit}
      title={`Edit ${title}`}
      aria-label={`Edit ${title}`}
      data-testid={`${testId}-edit-btn`}
      className="pencil-edit-btn"
      style={{
        background: 'transparent',
        border: '1px solid #e5e7eb',
        borderRadius: '4px',
        padding: '2px 6px',
        cursor: 'pointer',
        color: '#9ca3af',
        lineHeight: 1,
      }}
    >
      <Pencil size={12} />
    </button>
  );

  const lockBadge = (
    <Lock
      size={14}
      aria-label="Locked — voucher issued"
      data-testid={`${testId}-lock-icon`}
      style={{ color: '#9ca3af' }}
    />
  );

  let headerAction = null;
  if (locked) headerAction = lockBadge;
  else if (editable && !isEditing) headerAction = pencil;

  return (
    <SectionCard
      title={title}
      icon={icon}
      accent={accent}
      testId={testId}
      className={className}
      headerAction={headerAction}
    >
      {isEditing ? (
        <div data-testid={`${testId}-editor`}>
          <div style={{ fontSize: '11px', color: '#6b7280', marginBottom: '6px' }}>
            One item per line.
          </div>
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            rows={Math.max(4, items.length + 1)}
            autoFocus
            data-testid={`${testId}-textarea`}
            style={{
              width: '100%',
              resize: 'vertical',
              border: '2px solid #0891b2',
              borderRadius: '4px',
              padding: '8px 10px',
              outline: 'none',
              background: '#f0f9ff',
              fontSize: '14px',
              lineHeight: 1.45,
              fontFamily: 'Inter, sans-serif',
            }}
          />
          <div style={{ marginTop: '8px' }}>
            <button
              type="button"
              onClick={save}
              disabled={saving}
              data-testid={`${testId}-save`}
              style={{
                padding: '6px 16px',
                background: '#0891b2',
                color: '#fff',
                border: 'none',
                borderRadius: '4px',
                fontSize: '12px',
                fontWeight: 600,
                cursor: saving ? 'not-allowed' : 'pointer',
                opacity: saving ? 0.7 : 1,
              }}
            >
              {saving ? 'Saving…' : 'Save'}
            </button>
            <button
              type="button"
              onClick={cancel}
              data-testid={`${testId}-cancel`}
              style={{
                marginLeft: '6px',
                padding: '6px 14px',
                background: 'transparent',
                color: '#6b7280',
                border: '1px solid #d1d5db',
                borderRadius: '4px',
                fontSize: '12px',
                cursor: 'pointer',
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      ) : items.length === 0 ? (
        <div style={{ fontSize: '13px', fontStyle: 'italic', color: '#9ca3af' }}>
          No items — click the pencil to add
        </div>
      ) : useBullets ? (
        <BulletList items={items} kind={kind} />
      ) : (
        <InlineHtmlOrText value={value} />
      )}
    </SectionCard>
  );
}

// ---------------------------------------------------------------------------
// RefRow — reference row "LABEL : VALUE" via inline CSS-table (no flex, no
// full-width). When `editable` + `fieldKey` are provided the value becomes
// inline-editable via InlineEditableField — pencil sits next to the ref
// code. When `locked` is true a Lock icon is shown instead of the pencil.
// ---------------------------------------------------------------------------
export const RefRow = ({
  label,
  value,
  accent,
  editable = false,
  locked = false,
  fieldKey,
  onFieldSave,
}) => {
  if (!has(value) && !editable && !locked) return null;
  let valueCell;
  if (editable && fieldKey) {
    valueCell = (
      <InlineEditableField
        value={value || ''}
        variant="text"
        editable
        onSave={(v) => onFieldSave?.(fieldKey, v)}
        textareaRows={1}
        ariaLabel={`Edit ${label}`}
        testIdPrefix={`${fieldKey}-editable`}
        displayClassName="font-mono"
        displayStyle={{ fontSize: '12px', color: '#1e293b' }}
      />
    );
  } else if (locked && fieldKey) {
    valueCell = (
      <InlineEditableField
        value={value || ''}
        variant="text"
        locked
        ariaLabel={`${label} locked`}
        testIdPrefix={`${fieldKey}-editable`}
        displayClassName="font-mono"
        displayStyle={{ fontSize: '12px', color: '#1e293b' }}
      />
    );
  } else {
    valueCell = value;
  }
  return (
    <div style={{ display: 'block', marginBottom: '4px' }}>
      <div style={{ display: 'inline-table' }}>
        <span
          style={{
            display: 'table-cell',
            verticalAlign: 'baseline',
            whiteSpace: 'nowrap',
            paddingRight: '8px',
            fontSize: '10px',
            textTransform: 'uppercase',
            letterSpacing: '0.18em',
            fontWeight: 600,
            color: accent,
          }}
        >
          {label}
        </span>
        <span
          className={(editable || locked) && fieldKey ? '' : 'font-mono'}
          style={{
            display: 'table-cell',
            verticalAlign: 'baseline',
            whiteSpace: 'nowrap',
            fontSize: '12px',
            color: '#1e293b',
            wordBreak: 'break-all',
          }}
        >
          {valueCell}
        </span>
      </div>
    </div>
  );
};
